import { useContext } from "@nuxtjs/composition-api"
import { Severity } from "@sentry/types"
import { v4 as uuidv4 } from "uuid"

import { useTrackedEventMutation } from "@/api/query/trackedEventsQuery"
import { EventGoogleAdsIds, Events } from "@/enums"
import { TrackedEventData, UserAnalyticsID } from "@/types/models/TrackedEvent"
import { User } from "@/types/models/User"

const ID = "AW-11470038287"

function getGoogleAnalyticsID() {
  const pattern = new RegExp("_ga" + "=([^;]+)")
  const value = document.cookie.match(pattern)![1].split(".")
  return `${value[2]}.${value[3]}`
}

function generateUserUUID(): UserAnalyticsID {
  return uuidv4()
}

function registEvent(
  event: Events,
  data: TrackedEventData,
  userId: number | null = null,
) {
  const { app } = useContext()
  try {
    const { mutate: trackEvent } = useTrackedEventMutation()
    const userAnalyticsID = getUserAnalyticsID()
    const googleAnalyticsID = getGoogleAnalyticsID()
    const newData = {
      ...data,
      userId,
      userAnalyticsID,
      googleAnalyticsID,
    }

    app.$analytics.track(event, newData)
    trackEvent({ userAnalyticsID, googleAnalyticsID, event, data })
  } catch (error) {
    app.$sentry.captureEvent({
      message: error,
      level: Severity.Error,
    })
  }
}

function registGoogleAdsEvent(
  event: Events,
  userEmail: String | null,
  data: TrackedEventData,
) {
  if (userEmail) {
    gtagAds("set", "user_data", {
      email: userEmail,
    })
  }

  if (event === Events.CASE_PAYMENT || event === Events.SUBSCRIPTION_PAYMENT) {
    gtagAds("event", "conversion", {
      send_to: `${ID}/${getGoogleAdsId(event)}`,
      value: data.value,
      currency: data.currency,
      transaction_id: data.caseId,
    })
  } else {
    gtagAds("event", "conversion", {
      send_to: `${ID}/${getGoogleAdsId(event)}`,
    })
  }
}

function getGoogleAdsId(event: Events): EventGoogleAdsIds {
  switch (event) {
    case Events.SIGN_UP:
      return EventGoogleAdsIds.SIGN_UP
    case Events.LOGIN:
      return EventGoogleAdsIds.LOGIN
    case Events.VERIFY_EMAIL:
      return EventGoogleAdsIds.VERIFY_EMAIL
    case Events.CASE_OPEN:
      return EventGoogleAdsIds.CASE_OPEN
    case Events.CASE_MESSAGE:
      return EventGoogleAdsIds.CASE_MESSAGE
    case Events.CASE_PAYMENT:
      return EventGoogleAdsIds.CASE_PAYMENT
    case Events.SUBSCRIPTION_PAYMENT:
      return EventGoogleAdsIds.SUBSCRIPTION_PAYMENT
    case Events.PAGE_VISIT:
      return EventGoogleAdsIds.PAGE_VISIT
    default:
      throw new Error("Event does not have googleAdsId")
  }
}

export function getUserAnalyticsID(): UserAnalyticsID {
  const userAnalyticsID = localStorage.getItem("userAnalyticsID")
  if (userAnalyticsID) return userAnalyticsID

  const newUserAnalyticsID = generateUserUUID()
  localStorage.setItem("userAnalyticsID", newUserAnalyticsID)
  return newUserAnalyticsID
}

export function trackPageVisit() {
  try {
    const { route, store } = useContext()
    const pageName = route.value.name!!

    const user = store.getters["user/currentUser"] as User
    const data = { pageName }

    registEvent(Events.PAGE_VISIT, data, user ? user.id : null)
    registGoogleAdsEvent(Events.PAGE_VISIT, user ? user.email : null, data)
  } catch (error) {
    const { app } = useContext()
    app.$sentry.captureEvent({
      message: error,
      level: Severity.Error,
    })
  }
}

export function trackEvent(
  event: Events,
  data: TrackedEventData,
  context: any,
) {
  try {
    const user = context.store.getters["user/currentUser"] as User
    // DONT REGIST GOOGLE ANALYTICS EVENT HERE BECAUSE IT IS BEING TRACKED IN THE BE
    registGoogleAdsEvent(event, user ? user.email : null, data)
  } catch (error) {
    context.app.$sentry.captureEvent({
      message: error,
      level: Severity.Error,
    })
  }
}
